import { backoffDelay, type SyncEngine, type SyncResult } from '@/features/registrations/syncEngine'
import { checkInService, CheckInServiceError, type CheckInService } from './checkInService'
import { checkInStore as defaultStore, type CheckInStore } from './checkInStore'

const permanentMessages = {
  invalid: 'The server didn’t recognise this ticket or registration.',
  wrong_event: 'This attendee belongs to a different event.',
} as const

/**
 * Sends check-ins made offline, oldest first, one at a time. Each carries its
 * client UUID, so a resend after a timeout never records a second check-in.
 *
 * "Already checked in" is a normal outcome here, not a failure: another device
 * checked the attendee in first, and the server keeps this one as a conflict
 * for the record. Only a check-in the server can't place at all (an unknown
 * ticket, or the wrong event) needs a person's attention.
 */
export function createCheckInSyncEngine({
  store = defaultStore,
  service = checkInService,
  now = () => new Date(),
  isOnline = () => navigator.onLine,
}: {
  store?: CheckInStore
  service?: Pick<CheckInService, 'checkIn'>
  now?: () => Date
  isOnline?: () => boolean
} = {}): SyncEngine {
  const running = new Map<string, Promise<SyncResult>>()

  async function run(userId: string): Promise<SyncResult> {
    const result = { sent: 0, retrying: 0, needsAttention: 0, stoppedOffline: false }
    for (const record of await store.due(userId, now())) {
      if (!isOnline()) {
        result.stoppedOffline = true
        break
      }
      await store.markSyncing(record.clientCheckInId)
      try {
        const sent = await service.checkIn({
          clientCheckInId: record.clientCheckInId,
          eventId: record.eventId,
          method: record.method,
          registrationId: record.registrationId,
          checkedInAt: record.checkedInAt,
          origin: 'offline',
        })
        if (sent.result === 'invalid' || sent.result === 'wrong_event') {
          await store.markNeedsAttention(record.clientCheckInId, permanentMessages[sent.result])
          result.needsAttention += 1
          continue
        }
        await store.markSynced(record.clientCheckInId)
        result.sent += 1
      } catch (error) {
        if (error instanceof CheckInServiceError && error.kind === 'refused') {
          await store.markNeedsAttention(record.clientCheckInId, error.message)
          result.needsAttention += 1
          continue
        }
        const attempts = record.attempts + 1
        await store.markRetry(record.clientCheckInId, error instanceof CheckInServiceError ? error.message : 'The check-in could not be sent.', new Date(now().getTime() + backoffDelay(attempts)))
        result.retrying += 1
        break
      }
    }
    return result
  }

  return {
    sync(userId) {
      const current = running.get(userId)
      if (current) return current
      const next = run(userId).finally(() => running.delete(userId))
      running.set(userId, next)
      return next
    },
  }
}
